import { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { AppLoader } from '../../../../Components/common/AppLoader/AppLoader';
import { DoctorCard } from '../../../../Components/ui/DoctorCard/DoctorCard';
import {
  fetchDoctors,
  fetchSpecialties,
  fetchSpecializations,
} from '../../../../api/doctorApi';
import { getSpecialtyNames, mapDoctorToCard } from '../../../../utils/doctorHelpers';
import { useAdminDashboardCtx } from '../../hooks/AdminDashboardContext';
import { uploadDoctorProfilePic } from './adminListUploadApi';
import '../../../home/medicalexperts/medicalexperts.css';
import './admin-list-views.css';

const ALL_TAB = 'All';

export const AdminDoctorListScreen = () => {
  const { selectStep } = useAdminDashboardCtx();
  const [activeTab, setActiveTab] = useState(ALL_TAB);
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState({});
  const [uploadError, setUploadError] = useState('');

  const loadDoctors = useCallback(async () => {
    const [doctorRows, specialtyRows, specializationRows] = await Promise.all([
      fetchDoctors(),
      fetchSpecialties(),
      fetchSpecializations(),
    ]);
    setDoctors(doctorRows);
    setSpecialties(getSpecialtyNames(specialtyRows));
    setSpecializations(specializationRows);
    return doctorRows;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        await loadDoctors();
        if (!cancelled) setActiveTab(ALL_TAB);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load doctors.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [loadDoctors]);

  const filteredDoctors = useMemo(() => {
    const cards = doctors.map((doc) => mapDoctorToCard(doc, { specializations, noDefaults: true }));
    if (activeTab === ALL_TAB || !activeTab) return cards;
    return cards.filter((doc) => doc.medicalSpecialty === activeTab);
  }, [doctors, specializations, activeTab]);

  const handleUploadAvatar = useCallback(async (doctorId, file) => {
    const doctor = doctors.find((doc) => String(doc.doctor_id) === String(doctorId));
    if (!doctor) return;

    setUploadError('');
    setUploading((prev) => ({ ...prev, [doctorId]: true }));
    try {
      await uploadDoctorProfilePic(doctor, file);
      await loadDoctors();
    } catch (err) {
      setUploadError(err.message || 'Failed to upload image.');
    } finally {
      setUploading((prev) => ({ ...prev, [doctorId]: false }));
    }
  }, [doctors, loadDoctors]);

  return (
    <div className="admin-list-view">
      <section className="medical-experts">
        <Container fluid>
          <div className="medical-experts__top">
            <div>
              <h2 className="medical-experts__title">Doctor List</h2>
              <p className="medical-experts__subtitle">
                Upload profile photos for doctors shown on the public website.
              </p>
            </div>
            <Button
              type="button"
              className="medical-experts__manage"
              onClick={() => selectStep(38)}
            >
              Manage Doctors
            </Button>
          </div>

          <div className="medical-experts__tabs-wrap">
            <div className="medical-experts__tabs">
              <button
                type="button"
                className={`medical-experts__tab ${activeTab === ALL_TAB ? 'medical-experts__tab--active' : ''}`}
                onClick={() => setActiveTab(ALL_TAB)}
              >
                {ALL_TAB}
              </button>
              {specialties.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`medical-experts__tab ${activeTab === s ? 'medical-experts__tab--active' : ''}`}
                  onClick={() => setActiveTab(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {uploadError && (
            <p className="text-danger mb-3">{uploadError}</p>
          )}

          {loading && <AppLoader className="py-5" />}

          {!loading && error && (
            <p className="text-center py-4 text-danger">{error}</p>
          )}

          {!loading && !error && (
            <div className="medical-experts__scroll-wrap">
              <div className="medical-experts__track">
                {filteredDoctors.length > 0 ? (
                  filteredDoctors.map((doc) => (
                    <div key={doc.id} className="medical-experts__card-item">
                      <DoctorCard
                        {...doc}
                        hideActions
                        allowImageUpload
                        uploadingAvatar={uploading[doc.id]}
                        onUploadAvatar={(file) => handleUploadAvatar(doc.id, file)}
                      />
                    </div>
                  ))
                ) : (
                  <p className="medical-experts__empty">
                    No doctors found. Add doctors in Doctor Master.
                  </p>
                )}
              </div>
            </div>
          )}
        </Container>
      </section>
    </div>
  );
};

export default AdminDoctorListScreen;
